// Google base-map styles cannot select a single place ID. Keep their POI labels
// hidden and draw a small, filtered set of Places results on top instead.
export const toCoordinates = value => {
  const lat = typeof value?.lat === 'function' ? value.lat() : value?.lat;
  const lng = typeof value?.lng === 'function' ? value.lng() : value?.lng;
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const distanceMeters = (a, b) => {
  const radians = Math.PI / 180;
  const dLat = (b.lat - a.lat) * radians;
  const dLng = (b.lng - a.lng) * radians;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * radians) * Math.cos(b.lat * radians) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.asin(Math.sqrt(Math.min(1, h)));
};

export const filterNearbyLandmarks = (places, hiddenPlace) => {
  const hiddenPosition = toCoordinates(hiddenPlace?.position);
  if (!hiddenPlace?.id && !hiddenPosition) return [];
  const seen = new Set();
  return places.reduce((result, place) => {
    const position = toCoordinates(place.location);
    const name = place.displayName;
    const isHidden = hiddenPlace.id
      ? place.id === hiddenPlace.id
      : position && distanceMeters(position, hiddenPosition) <= 100;
    if (!place.id || !name || !position || isHidden || seen.has(place.id)) return result;
    seen.add(place.id);
    result.push({ id: place.id, name, position, attributions: place.attributions || [] });
    return result;
  }, []);
};

export const fetchNearbyLandmarks = async ({ center, radius, language, hiddenPlace }) => {
  if (!hiddenPlace?.id && !toCoordinates(hiddenPlace?.position)) return [];
  const placesLibrary = window.google?.maps?.places;
  // Use the already-loaded API/key. Do not reload Maps or change its restrictions.
  if (!placesLibrary?.Place?.searchNearby) return [];
  const { places = [] } = await placesLibrary.Place.searchNearby({
    fields: ['id', 'displayName', 'location'],
    locationRestriction: { center: toCoordinates(center), radius },
    maxResultCount: 20,
    rankPreference: 'POPULARITY',
    language,
  });
  return filterNearbyLandmarks(places, hiddenPlace);
};

// Web Mercator pixels, shared by the static image and the interactive overlay.
const worldPoint = ({ lat, lng }) => {
  const sin = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  return { x: (lng + 180) / 360, y: 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI) };
};

export const positionLandmarks = (landmarks, center, zoom, { width, height }) => {
  const origin = worldPoint(center);
  const scale = 256 * 2 ** zoom;
  const occupied = [];
  return landmarks.reduce((result, landmark) => {
    const point = worldPoint(landmark.position);
    const dx = point.x - origin.x;
    const x = (dx - Math.round(dx)) * scale + width / 2;
    const y = (point.y - origin.y) * scale + height / 2;
    // Fixed label boxes make collision handling identical in both map modes.
    // Reserve the bottom strip for Google's attribution and the left for controls.
    const box = { left: x - 70, right: x + 70, top: y - 8, bottom: y + 42 };
    if (box.left < 55 || box.right > width - 8 || box.top < 8 || box.bottom > height - 30) {
      return result;
    }
    if (
      occupied.some(
        b => box.left < b.right && box.right > b.left && box.top < b.bottom && box.bottom > b.top
      )
    ) {
      return result;
    }
    occupied.push(box);
    result.push({ ...landmark, x, y });
    return result;
  }, []);
};
